import { Action, createPath } from 'history';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import { updateHelpWidgetSession } from '../../common/api/updateHelpWidgetSession';
import { helpCenterSlice } from '../../common/reducers/helpCenter';
import { selectIsLiveChat } from '../../common/selectors/chat';
import { selectWidgetSessionState } from '../../common/selectors/widgetSession';
import { renderModel } from '../../util/renderModel';
import { appRoutes } from '../appRoutes';

const isLandingPage = renderModel.isLandingPage();
const isChatbotDisabled = renderModel.isChatbotDisabled();

export const useSyncUrlState = (): void => {
  const dispatch = useDispatch();
  const isLiveChat = useSelector(selectIsLiveChat);
  const [searchParams] = useSearchParams();
  const widgetSessionState = useSelector(selectWidgetSessionState);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const skipNextLocationChange = useRef<boolean>(false);

  const updateSession = useCallback(
    async ({ removeLast, replacedUrl, addedUrl }) => {
      await updateHelpWidgetSession({
        id: widgetSessionState.sessionTabId,
        removeLast,
        replacedUrl,
        addedUrl,
      });
    },
    [widgetSessionState.sessionTabId],
  );

  useEffect(() => {
    if (isLandingPage) {
      return;
    }

    // URL changed event(user navigates to other page in another browser tab)
    const { addedUrl, replacedUrl, removeLast } = widgetSessionState;

    if (addedUrl) {
      navigate(addedUrl);
    } else if (replacedUrl) {
      navigate(replacedUrl, { replace: true });
    } else if (removeLast) {
      navigate(-1);
    }

    skipNextLocationChange.current = true;
  }, [widgetSessionState]);

  useEffect(() => {
    if (skipNextLocationChange.current || isLandingPage) {
      skipNextLocationChange.current = false;
      return;
    }

    const url = createPath(location);

    switch (navigationType) {
      case Action.Pop:
        void updateSession({ removeLast: true });
        break;
      case Action.Push:
        void updateSession({ addedUrl: url });
        break;
      case Action.Replace:
        void updateSession({ replacedUrl: url });
        break;
      default:
        throw new Error(`Unexpected navigationType: ${navigationType}`);
    }
  }, [location]);

  useEffect(() => {
    if (isLiveChat && !isChatbotDisabled && !isLandingPage) {
      // we should always load chatbot screen until live conversation has ended
      navigate(appRoutes.chatbot());
    }
  }, []);

  useEffect(() => {
    const searchedText = searchParams.get('query');

    dispatch(
      helpCenterSlice.actions.setSearchedText(searchedText ?? undefined),
    );
  }, [dispatch, searchParams]);
};
